import * as React from 'react';

import { CloseOutlined as CancelIcon, CloudUploadOutlined as UploadIcon } from '@ant-design/icons';
import { Button } from 'antd';

import clsx from 'clsx';
import { useFormikContext } from 'formik';

const FILENAME_MAX_SIZE = 25;

type FileInputProps = {
  label: string;
  name: string;
  helperText?: React.ReactNode;
  validFormats?: string;
};

function FileInput({ name, label, helperText, validFormats }: FileInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { setFieldValue, values, errors, getFieldMeta, setFieldTouched } = useFormikContext();

  function handleClick() {
    inputRef?.current.click();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFieldTouched(name, true);
    const file = e.target.files[0];

    if (file) {
      setFieldValue(name, file);
    }
  }

  function cleanInput() {
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  }

  function handleRemove(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setFieldValue(name, null);
    cleanInput();
  }

  const value = (values as any)[name] as File | null;
  const hasValue = !!value;
  const errorMessage = (errors as any)[name] as string | null;
  const hasError = !!errorMessage && getFieldMeta(name)?.touched;

  const fileName =
    hasValue && (value.name.length > FILENAME_MAX_SIZE ? `${value.name.slice(0, FILENAME_MAX_SIZE)}...` : value.name);

  return (
    <div className='text-grey-500 font-sans'>
      <fieldset
        className={clsx(
          'relative cursor-pointer! rounded border! border-[var(--uizz-antd-color-border)]! bg-white pt-1! pb-[10px]! pl-3! hover:border-[var(--uizz-antd-select-hover-border-color)]! focus:shadow-[0_0_0_0.2rem_rgb(0_123_255/25%)]! [&_label]:absolute! [&_label]:-top-5! [&_label]:left-[24px]! [&_label]:text-xs! [&_label]:font-semibold! [&_legend]:invisible! [&_legend]:ml-[8px]! [&_legend]:text-[13.3px]!',
          hasError && 'border-error! [&_label]:text-error!'
        )}
        onClick={handleClick}
        role='button'
      >
        <label className='-mt-1' htmlFor={name}>
          {label}
        </label>
        <legend className='w-auto!'>{label}</legend>
        <input id={name} onChange={handleChange} ref={inputRef} type='file' hidden accept={validFormats} />
        <div className='relative -mt-0.5 flex items-center text-sm uppercase'>
          <UploadIcon className='mr-3' style={{ fontSize: 20 }} />
          {fileName || 'Selecione um arquivo'}
          {hasValue && (
            <Button
              type='text'
              size='small'
              className='absolute! right-3!'
              aria-label='Remover arquivo'
              onClick={handleRemove}
              icon={<CancelIcon style={{ fontSize: 20 }} />}
            />
          )}
        </div>
      </fieldset>
      <div className='mt-2 font-sans text-[10px]'>
        {hasError ? (
          <p role='alert' className='text-error'>
            {errorMessage}
          </p>
        ) : (
          <>
            {typeof helperText === 'string' ? (
              <p role='alert' className='text-[#072D83]'>
                {helperText}
              </p>
            ) : (
              helperText
            )}
          </>
        )}
      </div>
    </div>
  );
}

export { FileInput };
